/**
 * Self-test suite: Notification per-thread settings (LWW + mute-until)
 *
 * Mirrors NotificationThreadSettingsSelfTest.swift exactly. Cases grouped by
 * component:
 *
 *   Codec   (1-5)   — wire-format encode/decode round-trips + validation
 *   Store   (6-10)  — set/get, per-channel isolation, LWW, persistence
 *   Revert  (11-12) — lazy expired-mute revert semantics + no disk write
 *   Policy  (13-18) — pure-function decide() decision table
 *
 * Every case that touches IndexedDB uses a fresh DB name
 * (`kaether-selftest-<uuid>`) and cleans up after itself.
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import {
  NotificationThreadSettingsCodec,
  NOTIF_SETTINGS_WIRE_PREFIX,
} from "./NotificationThreadSettings";
import type { NotificationThreadSettingsPayload } from "./NotificationThreadSettings";
import { NotificationThreadSettingsStore } from "./NotificationThreadSettingsStore";
import { decide } from "./NotificationDeliveryPolicy";

// ---------------------------------------------------------------------------
// Fixtures
// ---------------------------------------------------------------------------

function dropDb(name: string): Promise<void> {
  return new Promise((resolve) => {
    const req = indexedDB.deleteDatabase(name);
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
    req.onblocked = () => resolve();
  });
}

function freshDb(): string {
  return `kaether-selftest-${crypto.randomUUID()}`;
}

// ---------------------------------------------------------------------------
// Codec tests (1–5)
// ---------------------------------------------------------------------------

function testCodecRoundTripAll(): TestCase {
  const payload: NotificationThreadSettingsPayload = {
    mode: "all",
    ts: 1_700_000_000_000,
  };
  const encoded = NotificationThreadSettingsCodec.encode(payload);
  const decoded = NotificationThreadSettingsCodec.decode(encoded);

  const prefixOk = encoded.startsWith(NOTIF_SETTINGS_WIRE_PREFIX);
  const modeOk = decoded?.mode === "all";
  const tsOk = decoded?.ts === payload.ts;
  const mutedUntilOk = decoded?.mutedUntil === undefined;
  const ok = prefixOk && modeOk && tsOk && mutedUntilOk;

  return ok
    ? pass("codec round-trip: mode=all")
    : fail(
        "codec round-trip: mode=all",
        `prefixOk=${prefixOk} modeOk=${modeOk} tsOk=${tsOk} mutedUntilOk=${mutedUntilOk}`,
      );
}

function testCodecRoundTripMentionsOnly(): TestCase {
  const payload: NotificationThreadSettingsPayload = {
    mode: "mentionsOnly",
    ts: 1_700_000_001_000,
  };
  const decoded = NotificationThreadSettingsCodec.decode(
    NotificationThreadSettingsCodec.encode(payload),
  );
  const ok = decoded?.mode === "mentionsOnly" && decoded?.ts === payload.ts;
  return ok
    ? pass("codec round-trip: mode=mentionsOnly")
    : fail("codec round-trip: mode=mentionsOnly", `mode=${decoded?.mode} ts=${decoded?.ts}`);
}

function testCodecRoundTripMutedWithUntil(): TestCase {
  const mutedUntil = Date.now() + 3_600_000; // 1 hour from now
  const payload: NotificationThreadSettingsPayload = {
    mode: "muted",
    mutedUntil,
    ts: 1_700_000_002_000,
  };
  const decoded = NotificationThreadSettingsCodec.decode(
    NotificationThreadSettingsCodec.encode(payload),
  );
  const ok =
    decoded?.mode === "muted" &&
    decoded?.mutedUntil === mutedUntil &&
    decoded?.ts === payload.ts;
  return ok
    ? pass("codec round-trip: mode=muted with mutedUntil")
    : fail(
        "codec round-trip: mode=muted with mutedUntil",
        `mode=${decoded?.mode} mutedUntil=${decoded?.mutedUntil} ts=${decoded?.ts}`,
      );
}

function testCodecRoundTripNone(): TestCase {
  const payload: NotificationThreadSettingsPayload = {
    mode: "none",
    ts: 1_700_000_003_000,
  };
  const decoded = NotificationThreadSettingsCodec.decode(
    NotificationThreadSettingsCodec.encode(payload),
  );
  const ok = decoded?.mode === "none" && decoded?.ts === payload.ts;
  return ok
    ? pass("codec round-trip: mode=none")
    : fail("codec round-trip: mode=none", `mode=${decoded?.mode} ts=${decoded?.ts}`);
}

function testCodecMutedUntilNegativeRejectedOnEncode(): TestCase {
  let threw = false;
  try {
    NotificationThreadSettingsCodec.encode({
      mode: "muted",
      mutedUntil: -1,
      ts: 1_700_000_004_000,
    });
  } catch {
    threw = true;
  }
  return threw
    ? pass("encode rejects negative mutedUntil")
    : fail("encode rejects negative mutedUntil", "expected encode to throw but it did not");
}

function testCodecMutedUntilNegativeRejectedOnDecode(): TestCase {
  // Bypass encode validation by crafting the wire string directly.
  const raw =
    NOTIF_SETTINGS_WIRE_PREFIX +
    JSON.stringify({ mode: "muted", mutedUntil: -42, ts: 1_700_000_005_000 });
  const decoded = NotificationThreadSettingsCodec.decode(raw);
  return decoded === null
    ? pass("decode rejects negative mutedUntil on wire")
    : fail(
        "decode rejects negative mutedUntil on wire",
        `expected null but got mode=${decoded?.mode}`,
      );
}

function testCodecMutedUntilUndefinedForNonMutedAccepted(): TestCase {
  // mutedUntil should be accepted as absent for mode=all
  const payload: NotificationThreadSettingsPayload = {
    mode: "all",
    ts: 1_700_000_006_000,
    // mutedUntil intentionally absent
  };
  let encoded: string;
  try {
    encoded = NotificationThreadSettingsCodec.encode(payload);
  } catch (err) {
    return fail(
      "undefined mutedUntil accepted for non-muted mode",
      `encode threw: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
  const decoded = NotificationThreadSettingsCodec.decode(encoded);
  const ok = decoded !== null && decoded.mode === "all" && decoded.mutedUntil === undefined;
  return ok
    ? pass("undefined mutedUntil accepted for non-muted mode")
    : fail(
        "undefined mutedUntil accepted for non-muted mode",
        `decoded=${decoded?.mode} mutedUntil=${decoded?.mutedUntil}`,
      );
}

// ---------------------------------------------------------------------------
// Store tests (6–10)
// ---------------------------------------------------------------------------

async function testStoreSetGetRoundTrip(): Promise<TestCase> {
  const dbName = freshDb();
  try {
    const store = new NotificationThreadSettingsStore(dbName);
    const payload: NotificationThreadSettingsPayload = {
      mode: "mentionsOnly",
      ts: Date.now(),
    };
    await store.set("ch1", payload);
    const mode = await store.get("ch1");
    const ok = mode === "mentionsOnly";
    return ok
      ? pass("store: set then get round-trip")
      : fail("store: set then get round-trip", `got mode=${mode}`);
  } finally {
    await dropDb(dbName);
  }
}

async function testStoreUnknownChannelReturnsAll(): Promise<TestCase> {
  const dbName = freshDb();
  try {
    const store = new NotificationThreadSettingsStore(dbName);
    const mode = await store.get("ch-never-written");
    const ok = mode === "all";
    return ok
      ? pass("store: unknown channel returns 'all'")
      : fail("store: unknown channel returns 'all'", `got mode=${mode}`);
  } finally {
    await dropDb(dbName);
  }
}

async function testStorePerChannelIsolation(): Promise<TestCase> {
  const dbName = freshDb();
  try {
    const store = new NotificationThreadSettingsStore(dbName);
    const ts = Date.now();
    await store.set("ch1", { mode: "muted", ts });
    await store.set("ch2", { mode: "none", ts: ts + 1 });

    const mode1 = await store.get("ch1");
    const mode2 = await store.get("ch2");
    const ok = mode1 === "muted" && mode2 === "none";
    return ok
      ? pass("store: per-channel isolation")
      : fail("store: per-channel isolation", `ch1=${mode1} ch2=${mode2}`);
  } finally {
    await dropDb(dbName);
  }
}

async function testStoreLWWOlderTsIgnored(): Promise<TestCase> {
  const dbName = freshDb();
  try {
    const store = new NotificationThreadSettingsStore(dbName);
    const laterTs = 2_000_000;
    const earlierTs = 1_000_000;

    // Write the later record first
    await store.set("ch1", { mode: "muted", ts: laterTs });
    // Attempt to overwrite with an earlier ts — must be ignored
    const written = await store.set("ch1", { mode: "none", ts: earlierTs });
    const mode = await store.get("ch1");

    const ok = !written && mode === "muted";
    return ok
      ? pass("store LWW: older ts is ignored")
      : fail(
          "store LWW: older ts is ignored",
          `written=${written} mode=${mode} (expected written=false, mode=muted)`,
        );
  } finally {
    await dropDb(dbName);
  }
}

async function testStorePersistenceAcrossInstances(): Promise<TestCase> {
  const dbName = freshDb();
  try {
    const writer = new NotificationThreadSettingsStore(dbName);
    await writer.set("ch1", { mode: "mentionsOnly", ts: 1_000_000 });

    const reader = new NotificationThreadSettingsStore(dbName);
    const mode = await reader.get("ch1");
    const ok = mode === "mentionsOnly";
    return ok
      ? pass("store: persists across fresh instances")
      : fail("store: persists across fresh instances", `got mode=${mode}`);
  } finally {
    await dropDb(dbName);
  }
}

// ---------------------------------------------------------------------------
// Lazy expired-mute revert tests (11–12)
// ---------------------------------------------------------------------------

async function testLazyRevertExpiredMuteReturnsAll(): Promise<TestCase> {
  const dbName = freshDb();
  try {
    const store = new NotificationThreadSettingsStore(dbName);
    const now = Date.now();
    // mutedUntil is 10 seconds in the past
    const mutedUntil = now - 10_000;

    await store.set("ch1", { mode: "muted", mutedUntil, ts: now });

    // get() with now > mutedUntil must return 'all' (lazy revert)
    const mode = await store.get("ch1", now);
    const ok = mode === "all";
    return ok
      ? pass("lazy revert: expired muted → get() returns 'all'")
      : fail(
          "lazy revert: expired muted → get() returns 'all'",
          `got mode=${mode} (expected 'all')`,
        );
  } finally {
    await dropDb(dbName);
  }
}

async function testLazyRevertDoesNotWriteBack(): Promise<TestCase> {
  const dbName = freshDb();
  try {
    const store = new NotificationThreadSettingsStore(dbName);
    const now = Date.now();
    const mutedUntil = now - 10_000; // already expired

    await store.set("ch1", { mode: "muted", mutedUntil, ts: now });

    // Trigger the lazy revert
    await store.get("ch1", now);

    // Read the raw on-disk record — it must still be 'muted'
    const raw = await store.getRaw("ch1");
    const ok = raw !== null && raw.mode === "muted" && raw.mutedUntil === mutedUntil;
    return ok
      ? pass("lazy revert: on-disk record remains 'muted' after get()")
      : fail(
          "lazy revert: on-disk record remains 'muted' after get()",
          `raw.mode=${raw?.mode} raw.mutedUntil=${raw?.mutedUntil}`,
        );
  } finally {
    await dropDb(dbName);
  }
}

// ---------------------------------------------------------------------------
// Policy tests (13–18)
// ---------------------------------------------------------------------------

function testPolicyAllWithMention(): TestCase {
  const decision = decide("all", true);
  return decision === "deliver"
    ? pass("policy: all + mention → deliver")
    : fail("policy: all + mention → deliver", `got ${decision}`);
}

function testPolicyAllWithoutMention(): TestCase {
  const decision = decide("all", false);
  return decision === "deliver"
    ? pass("policy: all + no mention → deliver")
    : fail("policy: all + no mention → deliver", `got ${decision}`);
}

function testPolicyMentionsOnlyWithMention(): TestCase {
  const decision = decide("mentionsOnly", true);
  return decision === "deliver"
    ? pass("policy: mentionsOnly + mention → deliver")
    : fail("policy: mentionsOnly + mention → deliver", `got ${decision}`);
}

function testPolicyMentionsOnlyWithoutMention(): TestCase {
  const decision = decide("mentionsOnly", false);
  return decision === "mentionOnlyFiltered"
    ? pass("policy: mentionsOnly + no mention → mentionOnlyFiltered")
    : fail(
        "policy: mentionsOnly + no mention → mentionOnlyFiltered",
        `got ${decision}`,
      );
}

function testPolicyMutedSuppresses(): TestCase {
  const withMention = decide("muted", true);
  const withoutMention = decide("muted", false);
  const ok =
    withMention === "suppressMuted" && withoutMention === "suppressMuted";
  return ok
    ? pass("policy: muted → suppressMuted (regardless of mention)")
    : fail(
        "policy: muted → suppressMuted (regardless of mention)",
        `withMention=${withMention} withoutMention=${withoutMention}`,
      );
}

function testPolicyNoneSuppresses(): TestCase {
  const withMention = decide("none", true);
  const withoutMention = decide("none", false);
  const ok =
    withMention === "suppressNoMode" && withoutMention === "suppressNoMode";
  return ok
    ? pass("policy: none → suppressNoMode (regardless of mention)")
    : fail(
        "policy: none → suppressNoMode (regardless of mention)",
        `withMention=${withMention} withoutMention=${withoutMention}`,
      );
}

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Notification per-thread settings (LWW + mute-until)",
  isVerified: true,
  run: async () =>
    buildSuiteResult([
      // Codec (1-7)
      testCodecRoundTripAll(),
      testCodecRoundTripMentionsOnly(),
      testCodecRoundTripMutedWithUntil(),
      testCodecRoundTripNone(),
      testCodecMutedUntilNegativeRejectedOnEncode(),
      testCodecMutedUntilNegativeRejectedOnDecode(),
      testCodecMutedUntilUndefinedForNonMutedAccepted(),
      // Store (8-12)
      await testStoreSetGetRoundTrip(),
      await testStoreUnknownChannelReturnsAll(),
      await testStorePerChannelIsolation(),
      await testStoreLWWOlderTsIgnored(),
      await testStorePersistenceAcrossInstances(),
      // Lazy revert (13-14)
      await testLazyRevertExpiredMuteReturnsAll(),
      await testLazyRevertDoesNotWriteBack(),
      // Policy (15-20)
      testPolicyAllWithMention(),
      testPolicyAllWithoutMention(),
      testPolicyMentionsOnlyWithMention(),
      testPolicyMentionsOnlyWithoutMention(),
      testPolicyMutedSuppresses(),
      testPolicyNoneSuppresses(),
    ] as TestCase[]),
});
